import { useMemo } from 'react'
import { Building2, Check, ChevronDown } from 'lucide-react'
import { useAuthStore } from '@/stores/authStore'
import { useUiStore } from '@/stores/uiStore'
import { useLookup } from '@/hooks/useLookup'
import { cn } from '@/lib/utils'
import { Select } from '@/components/ui/select'

/**
 * Which legal entity the screens are showing.
 *
 * <p>Only rendered when the signed-in user actually holds more than one. A switcher with a
 * single option is a control that teaches people to ignore it, and in the common
 * single-entity installation it would be permanent furniture that never does anything.
 *
 * <p>This narrows what is displayed; it does not widen it. The server decides what a caller
 * may see from their grants, and row-level security enforces that underneath — picking an
 * entity here cannot reach one the user was not granted.
 */
export function EntitySwitcher() {
  const legalEntityIds = useAuthStore((state) => state.user?.legalEntityIds)
  const activeEntityId = useUiStore((state) => state.activeEntityId)
  const setActiveEntity = useUiStore((state) => state.setActiveEntity)

  const entities = useLookup<{ id: string; name: string }>('legalEntities')

  const available = useMemo(() => {
    const rows = entities.data ?? []
    if (!legalEntityIds || legalEntityIds.length === 0) {
      // No narrowing granted means group-wide: every entity the server returned.
      return rows
    }
    return rows.filter((entity) => legalEntityIds.includes(entity.id))
  }, [entities.data, legalEntityIds])

  if (available.length < 2) {
    return null
  }

  const active = available.find((entity) => entity.id === activeEntityId) ?? available[0]

  return (
    <div className="relative">
      <label className="sr-only" htmlFor="entity-switcher">
        Legal entity
      </label>
      <div className="pointer-events-none absolute inset-y-0 start-0 flex items-center ps-3">
        <Building2 className="size-4 text-[var(--color-muted)]" aria-hidden="true" />
      </div>
      <Select
        id="entity-switcher"
        value={active?.id ?? ''}
        onChange={(event) => setActiveEntity(event.target.value)}
        className={cn(
          'h-9 appearance-none rounded-[var(--radius-control)] border border-[var(--color-border)]',
          'bg-[var(--color-card)] ps-9 pe-8 text-sm text-[var(--color-heading)]',
          'focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-[var(--color-primary)]',
        )}
      >
        {available.map((entity) => (
          <option key={entity.id} value={entity.id}>
            {entity.name}
          </option>
        ))}
      </Select>
      <div className="pointer-events-none absolute inset-y-0 end-0 flex items-center pe-2">
        <ChevronDown className="size-4 text-[var(--color-muted)]" aria-hidden="true" />
      </div>
      <Check className="sr-only" aria-hidden="true" />
    </div>
  )
}
